import { useRef, useEffect, useState } from "react";

export interface UseKeyboardViewportOptions {
  /** Top offset for maxHeight when keyboard is open. Default 24. */
  topOffsetWhenKeyboard?: number;
  /** Fraction of keyboard height to shift up (0–1). Smaller = dialog sits lower (closer to keyboard). Default 0.5. Use ~0.35 for category dialogs. */
  shiftFraction?: number;
}

export interface ViewportStyles {
  maxHeight?: string;
  transform?: string;
}

/**
 * Hook to adjust dialog position when mobile keyboard opens (visualViewport shrinks).
 * Uses transform so the dialog moves smoothly from its current position without layout jumps.
 */
export function useKeyboardViewport(
  open: boolean,
  options?: UseKeyboardViewportOptions
): { paperRef: React.RefObject<HTMLDivElement | null>; viewportStyles: ViewportStyles } {
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [viewportStyles, setViewportStyles] = useState<ViewportStyles>({});
  const topOffset = options?.topOffsetWhenKeyboard ?? 24;
  const shiftFraction = options?.shiftFraction ?? 0.5;

  useEffect(() => {
    if (!open || typeof window === "undefined" || !window.visualViewport) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    const updatePosition = (): void => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        const vvHeight = vv.height;
        const winHeight = window.innerHeight;
        const keyboardOpen = vvHeight < winHeight * 0.75;
        if (keyboardOpen) {
          const shiftUp = Math.round((winHeight - vvHeight) * shiftFraction);
          setViewportStyles({
            maxHeight: `${vvHeight - topOffset}px`,
            transform: `translateY(-${shiftUp}px)`,
          });
        } else {
          setViewportStyles({});
        }
      }, 120);
    };
    updatePosition();
    window.visualViewport.addEventListener("resize", updatePosition);
    window.visualViewport.addEventListener("scroll", updatePosition);
    return () => {
      clearTimeout(timeoutId);
      const vv = window.visualViewport;
      if (vv) {
        vv.removeEventListener("resize", updatePosition);
        vv.removeEventListener("scroll", updatePosition);
      }
      setViewportStyles({});
    };
  }, [open, topOffset, shiftFraction]);

  return { paperRef, viewportStyles };
}
